import { FC, useState } from 'react';
import { Patient, Encounter } from '@medplum/fhirtypes';
import { formatDateTime, formatCodeableConcept } from '@medplum/core';
import {
  Stack,
  Text,
  Box,
  Group,
  Skeleton,
  Anchor,
  Card,
  Flex,
  Paper,
} from '@mantine/core';
import EncounterImageCarousel from './EncounterImageCarousel';
import EncounterImageUploadModal from './EncounterImageUploadModal';
import { usePatientEncounters } from '@/hooks/usePatientEncounters';
import { useEncounterMedia } from '@/hooks/useEncounterMedia';
import { usePatientAge } from '@/hooks/usePatientAge';
import { useTranslation } from 'react-i18next';
import { StatusBadge } from '@/components/medplum-react/StatusBadge/StatusBadge';

interface EncounterImagesListProps {
  patient?: Patient;
}

interface EncounterImageBoxProps {
  encounter: Encounter;
  patient: Patient;
  onAddImages: (encounter: Encounter) => void;
}

const EncounterImageBox: FC<EncounterImageBoxProps> = ({
  encounter,
  patient,
  onAddImages,
}) => {
  const age = usePatientAge(patient, encounter);
  const { images, loading } = useEncounterMedia(encounter);
  const { t } = useTranslation(['images-tab']);
  const statusLabel = t(`statusOptions.${encounter.status}`, {
    ns: 'encounters',
    defaultValue: encounter.status.replace(/-/g, ' '),
  });
  const ageString = [];
  if (age?.years) {
    ageString.push(
      `${age?.years} ${
        age?.years > 1
          ? t('years', { ns: 'images-tab' })
          : t('year', { ns: 'images-tab' })
      }`
    );
  }
  if (age?.months) {
    ageString.push(
      `${age?.months} ${
        age?.months > 1
          ? t('months', { ns: 'images-tab' })
          : t('month', { ns: 'images-tab' })
      }`
    );
  }
  if (age?.days) {
    ageString.push(
      `${age?.days} ${
        age?.days > 1
          ? t('days', { ns: 'images-tab' })
          : t('day', { ns: 'images-tab' })
      }`
    );
  }

  return (
    <Card shadow="xs" padding="md" radius="md" withBorder>
      <Group justify="space-between" align="flex-start" wrap="nowrap">
        <Box>
          <Text fw={600} size="md">
            {encounter.period?.start
              ? formatDateTime(encounter.period.start)
              : t('noDate', { ns: 'images-tab' })}
          </Text>
          <Text size="sm" c="dimmed" mb={4}>
            {encounter.type ? formatCodeableConcept(encounter.type[0]) : '-'}
          </Text>
          <StatusBadge status={encounter.status} label={statusLabel} />
          <Text size="sm" mt={4}>
            {ageString.join(', ') ?? t('noAge', { ns: 'images-tab' })}
          </Text>
        </Box>
        <Flex
          miw={200}
          maw={420}
          flex={1}
          justify="flex-end"
          direction="column"
        >
          {loading ? (
            <Skeleton height={80} radius="md" />
          ) : images.length > 0 ? (
            <Box>
              <Group justify="flex-end" mb={'xs'}>
                <Anchor
                  component="button"
                  onClick={() => onAddImages(encounter)}
                >
                  {t('add', { ns: 'images-tab' })}
                </Anchor>
              </Group>
              <EncounterImageCarousel
                images={images}
                encounter={encounter}
                age={ageString.join(', ')}
                deletable
              />
            </Box>
          ) : (
            <Box>
              <Group justify="flex-end" mb={'xs'}>
                <Anchor
                  component="button"
                  onClick={() => onAddImages(encounter)}
                >
                  {t('add', { ns: 'images-tab' })}
                </Anchor>
              </Group>
              <Paper w="100%" h={80} bg="#f3f3f3" radius={8}>
                <Flex align="center" justify="center" h="100%">
                  <Text size="xs" color="dimmed">
                    {t('noImages', { ns: 'images-tab' })}
                  </Text>
                </Flex>
              </Paper>
            </Box>
          )}
        </Flex>
      </Group>
    </Card>
  );
};

const EncounterImagesList: FC<EncounterImagesListProps> = ({ patient }) => {
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [selectedEncounter, setSelectedEncounter] = useState<Encounter | null>(
    null
  );
  const { encounters, loading } = usePatientEncounters(patient);
  const { t } = useTranslation(['images-tab']);

  //----------------------------------------------------------------------------
  const handleAddImages = (encounter: Encounter) => {
    setSelectedEncounter(encounter);
    setUploadModalOpen(true);
  };

  if (!patient) {
    return (
      <Text color="dimmed">{t('noPatientSelected', { ns: 'images-tab' })}</Text>
    );
  }

  return (
    <Stack>
      {loading ? (
        <Skeleton height={120} radius="md" />
      ) : encounters.length === 0 ? (
        <Text ta="center" c="dimmed">
          {t('noEncountersFound', { ns: 'images-tab' })}
        </Text>
      ) : (
        encounters.map((encounter) => (
          <EncounterImageBox
            key={encounter.id}
            encounter={encounter}
            patient={patient}
            onAddImages={handleAddImages}
          />
        ))
      )}
      <EncounterImageUploadModal
        opened={uploadModalOpen}
        onClose={() => setUploadModalOpen(false)}
        encounter={selectedEncounter}
        patient={patient}
      />
    </Stack>
  );
};

export default EncounterImagesList;
